import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { Icon, V2CheckmarkIcon } from '@paypalcorp/pp-react-icons';
import { useTheme } from '@paypalcorp/pp-react-theme';
import { Shimmer } from '@paypalcorp/pp-react-utils';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import '../__generated__/styles/icon-button.css';

var styles = {"ppvx_icon-button":"ppvx_icon-button___1-6-9","ppvx_icon-button--primary":"ppvx_icon-button--primary___1-6-9","ppvx_icon-button--inverse":"ppvx_icon-button--inverse___1-6-9","ppvx_icon-button--size_sm":"ppvx_icon-button--size_sm___1-6-9","ppvx_icon-button--secondary":"ppvx_icon-button--secondary___1-6-9","ppvx_icon-button--tertiary":"ppvx_icon-button--tertiary___1-6-9","ppvx_icon-button__label":"ppvx_icon-button__label___1-6-9","ppvx_icon-button__icon":"ppvx_icon-button__icon___1-6-9","ppvx_icon-button__shimmer":"ppvx_icon-button__shimmer___1-6-9","ppvx--v2":"ppvx--v2___1-6-9","ppvx--v1":"ppvx--v1___1-6-9","ppvx_icon-button__loading-spinner":"ppvx_icon-button__loading-spinner___1-6-9","ppvx_icon-button--state_processing":"ppvx_icon-button--state_processing___1-6-9","ppvx_icon-button--state_success":"ppvx_icon-button--state_success___1-6-9","ppvx_icon-button--state__screenreader":"ppvx_icon-button--state__screenreader___1-6-9"};

var _excluded = ["as", "iconName", "svgIconComponent", "className", "iconClassName", "children", "buttonType", "btnState", "screenReaderText", "inverse", "size", "theme", "isLoading"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var IconButton = forwardRef(function (props, ref) {
  var _clsx;

  var as = props.as,
      iconName = props.iconName,
      svgIconComponent = props.svgIconComponent,
      className = props.className,
      iconClassName = props.iconClassName,
      children = props.children,
      _props$buttonType = props.buttonType,
      buttonType = _props$buttonType === void 0 ? 'primary' : _props$buttonType,
      _props$btnState = props.btnState,
      btnState = _props$btnState === void 0 ? 'initial' : _props$btnState,
      screenReaderText = props.screenReaderText,
      inverse = props.inverse,
      size = props.size,
      theme = props.theme,
      isLoading = props.isLoading,
      rest = _objectWithoutProperties(props, _excluded); // If theme is not directly provided, get from themeContext


  var _theme = useTheme('IconButton', theme);

  var isV2 = _theme && _theme === 'v2';

  if (_theme !== 'v2') {
    console.warn('Icon Button 1.0 styles are deprecated. Use Icon Button 2.0 styles and refer to our Migration Guide on how to use 2.0 styles/components.');
  }

  if (isV2 && isLoading) {
    var _cls = clsx(styles['ppvx_icon-button__shimmer'], size && styles["ppvx_icon-button--size_".concat(size)], className);

    return /*#__PURE__*/React.createElement(Shimmer, {
      className: _cls,
      isInverse: inverse,
      hasStyleDefaults: false
    }, children);
  }

  var cls = clsx(styles['ppvx_icon-button'], styles["ppvx_icon-button--".concat(buttonType)], (_clsx = {}, _defineProperty(_clsx, styles['ppvx_icon-button--inverse'], inverse), _defineProperty(_clsx, styles["ppvx_icon-button--size_".concat(size)], size), _clsx), btnState !== 'initial' && isV2 && styles["ppvx_icon-button--state_".concat(btnState)], isV2 && styles['ppvx--v2'], className);
  var Component = as || 'button'; // Setting type to null will not add the attribute:

  var type = Component === 'button' ? 'button' : null;
  var iconClasses = clsx(styles['ppvx_icon-button__icon'], iconClassName);

  var componentProps = _objectSpread({
    className: cls,
    ref: ref,
    type: type,
    disabled: isV2 && btnState && btnState !== 'initial'
  }, rest);

  return /*#__PURE__*/React.createElement(Component, componentProps, isV2 && btnState !== 'initial' ? function renderButtonState() {
    var buttonStateComponent = /*#__PURE__*/React.createElement(V2CheckmarkIcon, {
      size: "sm"
    });

    if (btnState === 'processing') {
      var loadingSpinnerProps = {
        className: styles['ppvx_icon-button__loading-spinner'],
        inverse: buttonType === 'primary' || buttonType === 'tertiary' && inverse,
        theme: _theme
      };
      buttonStateComponent = /*#__PURE__*/React.createElement(LoadingSpinner, loadingSpinnerProps);
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, buttonStateComponent, /*#__PURE__*/React.createElement("span", {
      className: styles["ppvx_icon-button--state__screenreader"],
      role: "status"
    }, screenReaderText || btnState));
  }() : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(svgIconComponent || Icon, {
    className: iconClasses,
    name: iconName,
    size: "sm",
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("span", {
    className: styles['ppvx_icon-button__label']
  }, children)));
});

export { IconButton };
